import { Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useState } from 'react';
import type { FC } from 'react';

import { PasswordRequirementsList } from '../../../components/global/PasswordRequirementsList/PasswordRequirementsList.tsx';
import type { PasswordRequirement } from '../../../components/global/PasswordRequirementsList/PasswordRequirementsList.tsx';
import { PasswordInput } from '../../../components/reactHookFormMantine/PasswordInput.tsx';
import { useI18n } from '../../../hooks/useI18n.ts';
import type { FormInputsContent } from '../../FormInputsContent.ts';
import type { FormPassword } from './schema.ts';

export const Password: FC<FormInputsContent<FormPassword>> = ({ useFormValue: { control, watch } }) => {
  const { t } = useI18n();

  const [passwordVisible, { toggle }] = useDisclosure(false);
  const [showPasswordVerification, setShowPasswordVerification] = useState(false);
  const passwordValue = watch('password');

  const passwordRequirements: PasswordRequirement[] = [
    {
      check: (value) => value.length >= 8,
      key: t('password-requirement--must-have-eight-chars') ?? 'Must have at least 8 characters',
    },
    {
      check: (value) => /[0-9]/.test(value),
      key: t('password-requirement--must-contain-number') ?? 'Must contain at least one number',
    },
    {
      check: (value) => /[a-z]/.test(value),
      key: t('password-requirement--must-contain-lowercase') ?? 'Must contain a lowercase letter',
    },
    {
      check: (value) => /[A-Z]/.test(value),
      key: t('password-requirement--must-contain-uppercase') ?? 'Must contain an uppercase letter',
    },
    {
      check: (value) => /[!@#$%^&*()_+}{';'?>.<,]/.test(value),
      key: t('password-requirement--must-contain-special-char') ?? 'Must contain a special character (!@#$%^&*)',
    },
    {
      check: (value) => !/\s/.test(value),
      key: t('password-requirement--no-spaces-allowed') ?? 'No spaces allowed',
    },
  ];

  return (
    <Stack>
      <PasswordInput
        control={control}
        label={t('signup_label--password') ?? 'Password'}
        name="password"
        placeholder={t('signup_placeholder--password') ?? 'Password'}
        visible={passwordVisible}
        onBlur={() => passwordValue === '' && setShowPasswordVerification(false)}
        onFocus={() => setShowPasswordVerification(true)}
        onVisibilityChange={toggle}
      />
      {showPasswordVerification && (
        <PasswordRequirementsList requirements={passwordRequirements} value={passwordValue} />
      )}
    </Stack>
  );
};
